import 'rxjs/add/operator/switchMap';

import { Injectable } from '@angular/core';
import * as _ from 'lodash';
import * as moment from 'moment';
import { TranslateService } from 'ng2-translate';
import { Observable } from 'rxjs/Observable';

import { UserRole } from '../../domain/enums/user-role';
import { DistributionOverviewItem, SectorOverviewItem, SectorPolygon, WeekOfYear } from '../../domain/models';
import { JwtDecoded } from '../../domain/models/authentication/jwt-decoded';
import { AuthenticationDomainService } from '../../domain/services/authentication/authentication.domain.service';
import { DistributionDomainService } from '../../domain/services/distribution/distribution.domain.service';
import { SectorDomainService } from '../../domain/services/sector/sector.domain.service';
import Constants from '../../shared/constants';

@Injectable()
export class DistributionListService {
    private currentWeek: WeekOfYear;

    constructor(
        private distributionDomainService: DistributionDomainService,
        private sectorDomainService: SectorDomainService,
        private authenticationDomainService: AuthenticationDomainService,
        private translate: TranslateService
    ) {
        this.currentWeek = null;
    }

    public getDistributionOverviewByWeekNumber(weekNumber: number): Observable<DistributionOverviewItem[]> {
        return this.distributionDomainService.getDistributionOverviewByWeekNumber(weekNumber)
            .switchMap((distributions: DistributionOverviewItem[]) => {
                const sectorIds: string[] = distributions.map((distribution: DistributionOverviewItem) => distribution.sectorId);
                return this.sectorDomainService.getSectorOverviewByIds(sectorIds);
            }, ((distributions: DistributionOverviewItem[], sectors: SectorOverviewItem[]): DistributionOverviewItem[] => {
                const updatedDistributions: DistributionOverviewItem[] = [...distributions];
                updatedDistributions.forEach((distribution: DistributionOverviewItem) => {
                    const distributionSector: SectorOverviewItem = sectors.find((sector: SectorOverviewItem) => sector.id === distribution.sectorId);
                    distribution.sectorDescription = distributionSector ? `${distributionSector.bdSectorId} ${distributionSector.name}` : '';
                });
                return updatedDistributions;
            }));
    }

    public getSectorById(id: string): Observable<SectorPolygon> {
        return this.sectorDomainService.getSectorById(id);
    }

    public getSectorsByIds(ids: string[]): Observable<SectorPolygon[]> {
        return this.sectorDomainService.getSectorsByIds(ids);
    }

    public getCurrentUserRoles(): UserRole[] {
        const jwtDecoded: JwtDecoded = this.authenticationDomainService.getDecodedAccessToken();
        if (!jwtDecoded || !jwtDecoded.role) {
            return null;
        }

        let result: UserRole[] = [];
        if (_.isArray(jwtDecoded.role)) {
            _.each(jwtDecoded.role, (r: string) => {
                result.push(UserRole[r]);
            });
        } else {
            result.push(UserRole[<string>jwtDecoded.role]);
        }
        return result;
    }

    public getWeekOfYearList(): Observable<WeekOfYear[]> {
        let result: WeekOfYear[] = [];
        let currentWeek: number = moment().isoWeek();

        return this.translate.get('WEEK').map((weekTranslation: string) => {
            for (let i: number = currentWeek - 7; i <= currentWeek; i++) {
                const start: moment.Moment = moment().isoWeek(i).startOf('isoWeek');
                const end: moment.Moment = moment().isoWeek(i).endOf('isoWeek');

                const numberOfWeeks: number = moment(`12-31-${moment().isoWeek(i).year()}`, 'MMDDYYYY').isoWeek() === 53 ? 53 : 52;
                result.push({
                    weekNumber: this.getNormalizedWeekNumber(i, numberOfWeeks),
                    start: start.toDate(),
                    end: end.toDate(),
                    displayValue: `${weekTranslation} ${this.getNormalizedWeekNumber(i, numberOfWeeks)}\u00A0\u00A0${start.format(Constants.MomentDateFormat)} - ${end.format(Constants.MomentDateFormat)}`
                });
            }
            return result.reverse();
        });
    }

    private getNormalizedWeekNumber(weekNumber: number, totalWeeks: number): number {
        if (weekNumber < 1) {
            weekNumber = totalWeeks + weekNumber;
        }
        return weekNumber;
    }

    public getCurrentWeek(): WeekOfYear {
        return this.currentWeek;
    }

    public setCurrentWeek(week: WeekOfYear): void {
        this.currentWeek = week;
    }
}



// WEBPACK FOOTER //
// ./src/app/modules/distribution/services/distribution-list.service.ts